import React, { useMemo } from "react"
import { Container, Row, Col } from "reactstrap"
import ReactApexChart from "react-apexcharts"
import { DashStyles, DashHead, DateChip, PALETTE, useChartFix } from "../Dashboard/dashkit"

/*
 * Project Management Overview (static) — modelled on Anil's "Project Invoicing Overview".
 * Projects by stage, invoicing by sales unit / activity, and a project details table.
 * Numbers are placeholders.
 */
const P = PALETTE

const STATS = [
  { label: "Ends in 30–90 Days", value: "1", color: P.amber },
  { label: "Fully Invoiced", value: "42", color: P.green },
  { label: "At Risk", value: "6", color: P.red },
]

const PROJECTS = [
  { id: "PROJ-000016", name: "Custom App Development", owner: "Manfred Yasmine", start: "31 Jan 2021", end: "09 Feb 2024", status: "Definition", total: 90, inv: 63 },
  { id: "PROJ-000024", name: "Application Security", owner: "Manfred Yasmine", start: "31 Jan 2021", end: "09 Feb 2024", status: "On Going", total: 71, inv: 52 },
  { id: "PROJ-000097", name: "Custom App Development", owner: "Mahesh Timoteo", start: "01 Aug 2021", end: "31 Jul 2023", status: "Definition", total: 19, inv: 16 },
  { id: "PROJ-000104", name: "Web Application", owner: "Somaya Yolanda", start: "05 Aug 2021", end: "30 Apr 2023", status: "On Going", total: 4, inv: 1 },
  { id: "PROJ-000128", name: "Application Security", owner: "Giuseppina Boutros", start: "01 Dec 2021", end: "31 Jan 2025", status: "Definition", total: 40, inv: 18 },
]
const STATUS_STYLE = { "On Going": { bg: "#ecfdf5", fg: "#16a34a" }, Definition: { bg: "#eef2ff", fg: "#4f6ef7" } }

const ProjectOverview = ({ embedded = false }) => {
  if (!embedded) document.title = "Project Overview | Calyvern"
  useChartFix()

  const stageDonut = useMemo(() => ({
    options: {
      chart: { type: "donut", fontFamily: "inherit" },
      labels: ["On Going", "Project Definition"], colors: [P.indigo, "#c7d2fe"],
      legend: { show: false }, dataLabels: { enabled: false }, stroke: { width: 0 },
      plotOptions: { pie: { donut: { size: "72%", labels: { show: true, total: { show: true, label: "Total Projects", fontSize: "12px", color: "#94a3b8", formatter: () => "122" }, value: { fontSize: "24px", fontWeight: 700, color: "#0f172a" } } } } },
    },
    series: [64, 58],
  }), [])

  const bySalesUnit = useMemo(() => ({
    options: {
      chart: { type: "bar", stacked: true, toolbar: { show: false }, fontFamily: "inherit" },
      plotOptions: { bar: { columnWidth: "55%", borderRadius: 3 } },
      colors: [P.indigo, "#cbd5e1"], dataLabels: { enabled: false }, legend: { show: false },
      xaxis: { categories: ["Consult", "Infra", "Management", "Paperwork", "Platform", "Security", "Subscriptions", "Support"], labels: { rotate: -35, style: { colors: "#94a3b8", fontSize: "10px" } } },
      yaxis: { labels: { formatter: v => `${v}M`, style: { colors: "#94a3b8" } } },
      grid: { borderColor: "#eef0f4", strokeDashArray: 4 },
    },
    series: [
      { name: "Invoiced", data: [1.2, 0, 1.7, 0.8, 1.4, 1.3, 0.05, 3.5] },
      { name: "To be invoiced", data: [0.57, 0, 0.84, 0, 0.63, 0, 0, 3.2] },
    ],
  }), [])

  const byActivity = useMemo(() => ({
    options: {
      chart: { type: "bar", toolbar: { show: false }, fontFamily: "inherit" },
      plotOptions: { bar: { columnWidth: "58%", borderRadius: 3 } },
      colors: [P.indigo], legend: { show: false },
      xaxis: { categories: ["App Platform", "Consulting", "Cloud", "Customer Sup.", "Cybersec", "Data", "Doc Mgmt", "Infra Sup.", "IT Infra", "IT Sup."], labels: { rotate: -40, style: { colors: "#94a3b8", fontSize: "9.5px" } } },
      yaxis: { min: 0, max: 1.4, tickAmount: 7, forceNiceScale: true, labels: { formatter: v => `${(+v).toFixed(1)}M`, style: { colors: "#94a3b8" } } },
      dataLabels: { enabled: true, formatter: v => `${(+v).toFixed(2)}`, offsetY: -14, style: { fontSize: "9px", colors: ["#64748b"] } },
      grid: { borderColor: "#eef0f4", strokeDashArray: 4 },
    },
    series: [{ name: "Invoiced amount", data: [0.14, 1.01, 0.02, 1.28, 0.47, 0.27, 0.57, 0.79, 0.44, 0.35] }],
  }), [])

  return (
    <React.Fragment>
      <DashStyles />
      <div className={embedded ? "dk-wrap" : "page-content dk-wrap"}>
        <Container fluid>
          <DashHead title="Project Overview" welcome="Project pipeline, invoicing and milestones at a glance." right={<DateChip />} />

          <Row className="g-3">
            <Col xxl={3}>
              <div className="dk-card h-100">
                <div className="dk-card-head"><span>PROJECTS BY STAGE</span></div>
                <ReactApexChart options={stageDonut.options} series={stageDonut.series} type="donut" height={210} />
                <div className="dk-tiles mt-2" style={{ gridTemplateColumns: "1fr 1fr 1fr" }}>
                  {STATS.map(s => (
                    <div key={s.label} className="dk-tile" style={{ textAlign: "center", padding: "10px 6px" }}>
                      <div className="vl" style={{ color: s.color }}>{s.value}</div>
                      <div className="sub">{s.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </Col>
            <Col xxl={5}>
              <div className="dk-card h-100">
                <div className="dk-card-head"><span>INVOICING BY SALES UNIT</span></div>
                <div className="dk-legend"><span><i className="dk-dot" style={{ background: P.indigo }} />Invoiced</span><span><i className="dk-dot" style={{ background: "#cbd5e1" }} />To be invoiced</span></div>
                <ReactApexChart options={bySalesUnit.options} series={bySalesUnit.series} type="bar" height={260} />
              </div>
            </Col>
            <Col xxl={4}>
              <div className="dk-card h-100">
                <div className="dk-card-head"><span>INVOICED AMOUNT BY ACTIVITY</span></div>
                <ReactApexChart options={byActivity.options} series={byActivity.series} type="bar" height={280} />
              </div>
            </Col>
          </Row>

          <Row className="g-3 mt-1">
            <Col xs={12}>
              <div className="dk-card">
                <div className="dk-card-head"><span>PROJECT DETAILS</span><span className="dk-link">View All →</span></div>
                <div style={{ overflowX: "auto" }}>
                  <table className="dk-table">
                    <thead><tr><th>Project ID</th><th>Name</th><th>Owner</th><th>Start</th><th>End</th><th>Status</th><th>Milestones</th><th>Invoiced</th></tr></thead>
                    <tbody>
                      {PROJECTS.map(p => (
                        <tr key={p.id}>
                          <td style={{ fontWeight: 600, whiteSpace: "nowrap" }}>{p.id}</td>
                          <td>{p.name}</td>
                          <td style={{ color: "#64748b" }}>{p.owner}</td>
                          <td style={{ whiteSpace: "nowrap" }}>{p.start}</td>
                          <td style={{ whiteSpace: "nowrap" }}>{p.end}</td>
                          <td><span className="dk-badge" style={{ background: STATUS_STYLE[p.status].bg, color: STATUS_STYLE[p.status].fg }}>{p.status}</span></td>
                          <td>{p.total}</td>
                          <td>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <div className="dk-progress" style={{ width: 60 }}><span style={{ width: `${Math.round(p.inv / p.total * 100)}%`, background: P.green }} /></div>
                              <span style={{ fontSize: 11.5, color: "#64748b" }}>{p.inv}/{p.total}</span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  )
}

export default ProjectOverview
